import type { SagaAuditLogDto } from '@/dtos/ErrorHandlingSaga'
import { Box } from '@chakra-ui/react'
import { BaseEdge, EdgeLabelRenderer, type Edge, type EdgeProps } from '@xyflow/react'
import { EdgeLabel } from './auditEdge'
import { NODE_HEIGHT } from './layout'

// A `type` (not `interface`) so it satisfies React Flow's `Record<string, unknown>` edge-data constraint without an index signature.
export type SelfLoopEdgeData = {
  label?: string
  // Audit rows for this state's self-transition (PreviousState === NewState): retries, or failed deliveries that stayed put.
  steps: SagaAuditLogDto[]
}

export type SelfFlowEdge = Edge<SelfLoopEdgeData, 'selfLoopEdge'>

const LOOP_W = 22
const LOOP_H = 20

/**
 * A self-transition drawn as a compact arc above the node — React Flow collapses a same-node edge to a point, so the
 * arc is built by hand from the node geometry (the Left/Right handles sit at the node's vertical centre, so
 * sourceY − NODE_HEIGHT/2 is its top edge). Hovers list the attempts; the definition-only view shows the label.
 */
export const SelfLoopEdge = ({ sourceX, sourceY, targetX, markerEnd, style, data }: EdgeProps<SelfFlowEdge>) => {
  const centerX = (sourceX + targetX) / 2
  const topY = sourceY - NODE_HEIGHT / 2
  const apexY = topY - LOOP_H
  const x0 = centerX - LOOP_W / 2
  const x1 = centerX + LOOP_W / 2
  const path = `M ${x0},${topY} C ${x0},${apexY} ${x1},${apexY} ${x1},${topY}`

  const steps = data?.steps ?? []

  return (
    <>
      <BaseEdge path={path} markerEnd={markerEnd} style={style} />
      {(data?.label || steps.length > 0) && (
        <EdgeLabelRenderer>
          <Box
            className="nodrag nopan"
            position="absolute"
            transform={`translate(-50%, -50%) translate(${centerX}px, ${apexY}px)`}
            pointerEvents="all"
            zIndex={5}
          >
            <EdgeLabel steps={steps} />
          </Box>
        </EdgeLabelRenderer>
      )}
    </>
  )
}
